import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import CoreTab from './components/CoreTab';
import ModulesStatusTab from './components/ModulesStatusTab';
import IntegrationsTab from './components/IntegrationsTab';
import StorageStatusTab from './components/StorageStatusTab';
import PublicSitesTab from './components/PublicSitesTab';

// ── Types ──────────────────────────────────────────────────────────────────────
export interface SaasModule {
  id: string;
  name: string;
  category: string;
  is_core: boolean;
  is_addon: boolean;
  is_active: boolean;
  price_monthly: number;
}

export interface StatusData {
  // Core
  dbAlive: boolean;
  authAlive: boolean;
  tenantsTotal: number;
  tenantsActive: number;
  tenantsTrial: number;
  tenantsSuspended: number;
  plansCount: number;
  usersCount: number;
  auditLogsCount: number;

  // Modules
  allModules: SaasModule[];
  activeModulesCount: number;
  tenantModulesEnabled: number; // unique tenant_modules rows with is_enabled=true
  moduleRequestsPending: number;
  moduleRequestsApproved: number;
  moduleRequestsRejected: number;

  // Integrations
  integrationsEnabled: Record<string, number>; // integration_key → # of tenants enabled

  // Storage
  totalQuotaMb: number;
  totalUsedMb: number;
  tenantsOverQuota: number;
  tenantStorageList: { id: string; name: string; usedMb: number; quotaMb: number }[];

  // Public Sites
  tenantsWithSlug: number;
  domainRequestsPending: number;
  domainRequestsApproved: number;
  domainRequestsActive: number;
  domainRequestsRejected: number;
}

// ── Tab definitions ────────────────────────────────────────────────────────────
type TabId = 'core' | 'modules' | 'integrations' | 'storage' | 'sites';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'core',         label: 'Core',         icon: 'ri-server-line' },
  { id: 'modules',      label: 'Modules',      icon: 'ri-puzzle-line' },
  { id: 'integrations', label: 'Integrations', icon: 'ri-plug-line' },
  { id: 'storage',      label: 'Storage',      icon: 'ri-hard-drive-line' },
  { id: 'sites',        label: 'Public Sites', icon: 'ri-global-line' },
];

// ── Page ──────────────────────────────────────────────────────────────────────
export default function SAStatusPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading]   = useState(true);
  const [data, setData]         = useState<StatusData | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const rawTab = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = (rawTab && TABS.find((t) => t.id === rawTab)) ? rawTab : 'core';

  const setActiveTab = (id: TabId) => {
    if (id === 'core') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  };

  // ── Fetch all status data ──────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);

    // Test DB connection
    let dbAlive = false;
    let authAlive = false;
    try {
      const { error } = await supabase.from('saas_tenants').select('id', { count: 'exact', head: true });
      dbAlive = !error;
    } catch { dbAlive = false; }

    try {
      const { data: session } = await supabase.auth.getSession();
      authAlive = !!session?.session;
    } catch { authAlive = false; }

    // Parallel fetches
    const [
      tenantsRes, plansRes, modulesRes, usersRes, auditRes,
      tmEnabledRes, modReqRes, integrationsRes,
      storageRes, domainReqRes,
    ] = await Promise.all([
      supabase.from('saas_tenants').select('id, name, status, storage_used_mb, storage_quota_mb'),
      supabase.from('saas_plans').select('id', { count: 'exact', head: true }),
      supabase.from('saas_modules').select('id, name, category, is_core, is_addon, is_active, price_monthly').order('category').order('name'),
      supabase.from('saas_tenant_users').select('id', { count: 'exact', head: true }),
      supabase.from('activity_logs').select('id', { count: 'exact', head: true }),
      supabase.from('tenant_modules').select('module_id').eq('is_enabled', true),
      supabase.from('tenant_module_requests').select('id, status'),
      supabase.from('tenant_integrations').select('integration_key, is_enabled').eq('is_enabled', true),
      supabase.from('saas_tenants').select('id, name, storage_used_mb, storage_quota_mb').order('storage_used_mb', { ascending: false }),
      supabase.from('tenant_domain_requests').select('id, status'),
    ]);

    const tenants      = (tenantsRes.data  ?? []) as { id: string; name: string; status: string; storage_used_mb: number; storage_quota_mb: number }[];
    const allModules   = (modulesRes.data  ?? []) as SaasModule[];
    const tmEnabled    = tmEnabledRes.data ?? [];
    const modReqs      = (modReqRes.data   ?? []) as { id: string; status: string }[];
    const integrations = (integrationsRes.data ?? []) as { integration_key: string; is_enabled: boolean }[];
    const domainReqs   = (domainReqRes.data ?? []) as { id: string; status: string }[];
    const storageList  = (storageRes.data ?? []) as { id: string; name: string; storage_used_mb: number; storage_quota_mb: number }[];

    // Integration map: key → count
    const intMap: Record<string, number> = {};
    integrations.forEach((i) => {
      intMap[i.integration_key] = (intMap[i.integration_key] ?? 0) + 1;
    });

    // Storage metrics
    const totalQuotaMb = tenants.reduce((s, t) => s + (t.storage_quota_mb ?? 0), 0);
    const totalUsedMb  = tenants.reduce((s, t) => s + (t.storage_used_mb ?? 0), 0);
    const tenantsOverQuota = tenants.filter((t) => (t.storage_used_mb ?? 0) > (t.storage_quota_mb ?? 0)).length;

    setData({
      dbAlive,
      authAlive,
      tenantsTotal:     tenants.length,
      tenantsActive:    tenants.filter((t) => t.status === 'active').length,
      tenantsTrial:     tenants.filter((t) => t.status === 'trial').length,
      tenantsSuspended: tenants.filter((t) => ['suspended', 'cancelled'].includes(t.status)).length,
      plansCount:       plansRes.count ?? 0,
      usersCount:       usersRes.count ?? 0,
      auditLogsCount:   auditRes.count ?? 0,

      allModules,
      activeModulesCount: allModules.filter((m) => m.is_active).length,
      tenantModulesEnabled: tmEnabled.length,
      moduleRequestsPending:  modReqs.filter((r) => r.status === 'pending').length,
      moduleRequestsApproved: modReqs.filter((r) => r.status === 'approved').length,
      moduleRequestsRejected: modReqs.filter((r) => r.status === 'rejected').length,

      integrationsEnabled: intMap,

      totalQuotaMb,
      totalUsedMb,
      tenantsOverQuota,
      tenantStorageList: storageList.slice(0, 10).map((t) => ({
        id: t.id, name: t.name, usedMb: t.storage_used_mb ?? 0, quotaMb: t.storage_quota_mb ?? 0,
      })),

      tenantsWithSlug:        tenants.length, // all tenants have slugs
      domainRequestsPending:  domainReqs.filter((r) => r.status === 'pending').length,
      domainRequestsApproved: domainReqs.filter((r) => r.status === 'approved').length,
      domainRequestsActive:   domainReqs.filter((r) => r.status === 'active').length,
      domainRequestsRejected: domainReqs.filter((r) => r.status === 'rejected').length,
    });

    setLastChecked(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await fetchAll();
    };
    init();
  }, [navigate, fetchAll]);

  // ── Overall health dot ─────────────────────────────────────────────────────
  const overallOk = data ? data.dbAlive && data.authAlive : false;

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Status Center"
        subtitle="Estado real de la plataforma Pixory AI"
        actions={
          <div className="flex items-center gap-3">
            {/* Overall health pill */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${
              overallOk
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
              <span className={`w-2 h-2 rounded-full ${overallOk ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`}></span>
              {overallOk ? 'Platform Operational' : 'Checking…'}
            </div>

            {/* Refresh */}
            <button
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
            >
              <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
              Refresh
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-5 pb-8">
        {/* Last checked */}
        {lastChecked && (
          <p className="text-slate-400 text-xs mb-4 flex items-center gap-1.5">
            <i className="ri-time-line text-xs"></i>
            Last checked: {lastChecked.toLocaleTimeString()}
          </p>
        )}

        {/* ── Tab bar ── */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabId)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap cursor-pointer border-b-2 -mb-px transition-all ${
                activeTab === tab.id
                  ? 'text-[#2563eb] border-[#2563eb]'
                  : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`}></i>
              </span>
              {tab.label}
              {/* Pending badges */}
              {tab.id === 'modules' && data && data.moduleRequestsPending > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black text-[10px] font-bold inline-flex items-center justify-center">
                  {data.moduleRequestsPending}
                </span>
              )}
              {tab.id === 'sites' && data && data.domainRequestsPending > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black text-[10px] font-bold inline-flex items-center justify-center">
                  {data.domainRequestsPending}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {loading || !data ? (
          <LoadingState />
        ) : (
          <>
            {activeTab === 'core'         && <CoreTab data={data} />}
            {activeTab === 'modules'      && <ModulesStatusTab data={data} />}
            {activeTab === 'integrations' && <IntegrationsTab data={data} />}
            {activeTab === 'storage'      && <StorageStatusTab data={data} />}
            {activeTab === 'sites'        && <PublicSitesTab data={data} />}
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}
